import { useState, forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import { ViewMode } from "./viewMode";
import './greeting.css'
import './cv.css'
import { Link } from "react-router-dom";
import { ChildComponentRef } from "./childComponent";
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

type CvSection = 'Architecture' | 'Software' | 'Animation';

const CV = forwardRef((props, ref) => {
    const divRef = useRef<HTMLDivElement | null>(null);
    const [childComponentBrightness, setChildComponentBrightness] = useState<number>(0);
    const [childComponentFontSize, setChildComponentFontSize] = useState<number>(0);
    const [childComponentLocation, setChildComponentLocation] = useState<{x:number, y:number}>({x:0, y:0});
    const [childComponentSize, setChildComponentSize] = useState<{x:number, y:number}>({x:0, y:0});
    const [viewMode, setViewMode] = useState<ViewMode>();
    const [windowSize, setWindowSize] = useState<{width: number, height: number}>();
    const updateChildComponentBrightness = (gb: number) => setChildComponentBrightness(gb);
    const updateChildComponentFontSize = (gfs: number) => setChildComponentFontSize(gfs);
    const updateChildComponentLocation = (gl: {x:number, y:number}) => setChildComponentLocation(gl);
    const updateChildComponentSize = (gs: {x:number, y:number}) => setChildComponentSize(gs);
    const updateViewMode = (vm: ViewMode, width?: number, height?: number) => {
        setViewMode(vm);
        if (width && height) setWindowSize({width, height});
    }
    useImperativeHandle(ref, (): ChildComponentRef => ({
        name: 'CV', updateChildComponentBrightness, updateChildComponentLocation, updateChildComponentSize, updateChildComponentFontSize, updateViewMode, divRef
    }));

    const [selectedCvSection, setSelectedCvSection] = useState<CvSection>('Architecture');
    const handleSectionChange = (event: React.MouseEvent<HTMLElement>, newSection: CvSection,) => {
        if (newSection) {
            setSelectedCvSection(newSection);
        }
    };

    const headlineText = "CV";
    const cvIntroText = "Here, you'll find an overview of my professional journey, categorized into three fields: Architecture, Software Development, and Generative Art (Animation). Each section highlights my main activities, education, and works in these areas.";
    
    const componentStyle: React.CSSProperties = {
        position: "fixed",
        top: `${childComponentLocation.y}px`,
        left: `${childComponentLocation.x}px`,
        transform: "translate(-50%, 0%)",
        width: `${childComponentSize.x}px`,
        height: childComponentSize.y? `${childComponentSize.y}px` : 'auto',
        opacity: `${childComponentBrightness}`
    }
    const desktopStyle: React.CSSProperties = {
        padding: '20px 10px 0px 10px',
        backgroundColor: "rgba(0, 0, 0, .6)",
    }
    const mobileStyle: React.CSSProperties = {
        padding: '20px 10px 0px 10px',
        backgroundColor: "rgba(0, 0, 0, .6)",
    }
    const headlineTextStyle: React.CSSProperties = {
        margin: '0 0 10px 0',
        textAlign: "center",
        color: "white",
        fontFamily: "Roboto Condensed",
        fontWeight: 400,
        fontSize: `${childComponentFontSize + 5}px`,
    }
    const titleTextStyle: React.CSSProperties = {
        margin: '25px 0 5px 0',
        textAlign: "justify",
        color: "white",
        fontFamily: "Roboto Condensed",
        fontWeight: 400,
        fontSize: `${childComponentFontSize}px`,
    }
    const regularTextStyle: React.CSSProperties = {
        margin: 0,
        textAlign: "justify",
        color: "white",
        fontFamily: "Open Sans",
        fontWeight: 300,
        fontSize: `${childComponentFontSize - 8}px`,
    }

    const renderComponent = () => {
        switch (selectedCvSection) {
            case 'Architecture':
                return <Cv_architecture/>;
            case 'Software':
                return <Cv_software/>;
            case 'Animation':
                return <Cv_animation/>;
            default:
                return null;
        }
    };

    return (
        <div ref={divRef} style={componentStyle}>
            <div style={viewMode?.size == 'desktop'? desktopStyle : mobileStyle}>
                <h1 style={headlineTextStyle}>{headlineText}</h1>
                <p style={regularTextStyle}>{cvIntroText}</p>
                <ToggleButtonGroup value={selectedCvSection} exclusive onChange={handleSectionChange} aria-label="CV Section">
                    <ToggleButton className="CV" value="Architecture" aria-label="Architecture">
                        Architecture
                    </ToggleButton>
                    <ToggleButton className="CV" value="Software" aria-label="Software">
                        Software
                    </ToggleButton>
                    <ToggleButton className="CV" value="Animation" aria-label="Animation">
                        Animation
                    </ToggleButton>
                </ToggleButtonGroup>
                <div className="component-container">
                    {renderComponent()}
                </div>
            </div>
            <div className="links-container" style={{fontSize: `${childComponentFontSize}px`}}>
                <Link to="/" style={{marginRight: 'auto'}}>{"< Back"}</Link>
                <Link to="/about">About Me</Link>
                <Link to="/contacts" style={{marginLeft: 'auto'}}>Contacts</Link>
            </div>
        </div>
    )
});

export default CV

const Cv_architecture = () => {
    return (
        <div className="resume">
            <p>My primary interests in architecture encompass parametric design, BIM, and developing novel methodologies through software development. I have experience working on public, mixed-use, and industrial buildings.</p>
            <p><strong>Software:</strong> Grasshopper, Revit, Dynamo, SketchUp, VRay, Adobe Suite.</p>
            <h2>Education</h2>
            <ul>
                <li>
                    <small>2023 - Present</small>
                    <span>PhD Candidate - Technion, Israel Institute of Technology</span>
                    <small>Research Focus: Application of Machine Learning for Building Performance Optimization</small>
                </li>
                <li>
                    <small>2020 - 2023</small>
                    <span>Master of Architecture (MArch) - Technion, Israel Institute of Technology</span>
                    <small></small>
                </li>
                <li>
                    <small>2012 - 2017</small>
                    <span>Bachelor of Architecture (BArch) - Moscow Institute of Architecture (MARCHI)</span>
                    <small></small>
                </li>
            </ul>
            <h2>Experience</h2>
            <ul>
                <li>
                    <small>2022 - Present</small>
                    <span>Freelance Design Automation and Building Optimization Software Developer</span>
                    <small>Creation of Grasshopper, Dynamo, and Python scripts for architectural and engineering offices</small>
                </li>
                <li>
                    <small>2017 - 2020</small>
                    <span>Architect - Knafo Klimor Architects</span>
                    <small>Led BIM modeling in Revit, executed conceptual design and architectural documentation, and facilitated communication with consultants on both Israeli and international projects</small>
                </li>
                <li>
                    <small>2016</small>
                    <span>Architectural Visualization Specialist - ASADOV Studio</span>
                    <small>Visualization of architectural design concepts</small>
                </li>
                <li>
                    <small>2015</small>
                    <span>Intern - STRELKA KB</span>
                    <small>Urban Planning</small>
                </li>
            </ul>
            <h2>Conference Presentation</h2>
            <ul>
                <li>
                    <small>2023</small>
                    <span>International Building Performance Simulation Association (IBPSA) Conference, Shanghai</span>
                    <small>Presented research findings on the application of Machine Learning for sustainable architecture and building performance optimization</small>
                </li>
            </ul>
        </div>
    )
}

const Cv_software = () => {
    return (
        <div className="resume">
            <p>My journey into software development began during my architectural practice, where I developed scripts for parametric architecture and discovered my passion for coding. After taking introductory courses in computer science, I started research at the university to delve deeper into the possibilities of machine learning in architecture while extending my knowledge in various fields of computer science. During my resarch I got experience in training and implementing machine learning models. Currently, I work as a full-stack developer for web services, and I am interested in exploring the applications of WEB3 and the metaverse.</p>
            <p><strong>Programming Languages:</strong> JavaScript, TypeScript, GLSL, Python, Solidity.</p>
            <p><strong>Familiar with:</strong> C++, C#.</p>
            <p><strong>Frameworks and Libraries:</strong> NodeJS, React, NestJS, PyTorch.</p>
            <h2>Education</h2>
            <ul>
                <li>
                    <small>2022 - 2023</small>
                    <span>Bachelor of Computer Science - ITMO University</span>
                    <small></small>
                </li>
                <li>
                    <small>2020</small>
                    <span>CS50: Introduction to Computer Science - Harvard University</span>
                    <small>Online course</small>
                </li>
            </ul>
            <h2>Experience</h2>
            <ul>
                <li>
                    <small>2022 - Present</small>
                    <span>Freelance Full-Stack Software Developer</span>
                    <small>Developed web services for various clients and based on self-developed concepts.</small>
                </li>
            </ul>
        </div>
    )
}

const Cv_animation = () => {
    return (
        <div className="resume">
            <p>Generative art allows me to combine my creative and technological skills. My focus is on animations and interactive pieces created using JavaScript, GLSL, P5JS, TouchDesigner, and Grasshopper.</p>
            <ul>
                <li>
                    <small>2020 - Present</small>
                    <span>Parametric_Jam</span>
                    <small>Personal generative art project on Instagram.</small>
                </li>
                <li>
                    <small>2020 - Present</small>
                    <span>VJ sets for various local events.</span>
                    <small></small>
                </li>
                <li>
                    <small>2022</small>
                    <span>Total Strangers</span>
                    <small>Generative Art Collection at ArtBlocks.</small>
                </li>
                <li>
                    <small>2021</small>
                    <span>High Tide</span>
                    <small>Generative Art Collection at ArtBlocks.</small>
                </li>
                <li>
                    <small>2021</small>
                    <span>Low Tide</span>
                    <small>Generative Art Collection at ArtBlocks.</small>
                </li>
                <li>
                    <small>2021</small>
                    <span>NEWCOLLABORATIONS</span>
                    <small>Generative and Modern Art Installation in Chelyabinsk.</small>
                </li>
            </ul>
        </div>
    )
}

